// Rules-only constants for Snooker. The actual play (potting order, fouls,
// scoring) lives in controller.js - this just fixes the shared numbers both
// that file and the setup screen care about.

// Wedges 1-15 stand in for snooker's 15 reds, one wedge per red - a single
// scores 1, a double 2, a treble 3, but potting one (of any multiplier)
// always removes just that one red from the board, never more than one.
export const RED_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
export const TOTAL_REDS = 15;

// The actual color of a real snooker red ball - controller.js paints every
// red wedge this, in place of the dartboard's usual red/black, so the board
// reads as an actual set of snooker balls.
export const RED_COLOR = '#D6322F';

// The six colors live on 16-20 and the bull (25 = outer bull or bullseye,
// treated the same - see controller.js), each worth its own real snooker
// point value regardless of which wedge it happens to sit on, and painted
// on the board in that ball's own real color. Order here is potting order
// for the colors-only endgame.
export const COLORS = [
    { name: 'Yellow', number: 16, value: 2, color: '#FFD500' },
    { name: 'Green', number: 17, value: 3, color: '#1E824C' },
    { name: 'Brown', number: 18, value: 4, color: '#8B5E34' },
    { name: 'Blue', number: 19, value: 5, color: '#1D5DBF' },
    { name: 'Pink', number: 20, value: 6, color: '#E8608A' },
    { name: 'Black', number: 25, value: 7, color: '#141414' }
];

// A foul always costs at least 4, same floor as real snooker, or the value
// of whichever ball was actually potted out of turn if that's higher.
export const MIN_FOUL = 4;

// Whether a double/treble multiplies a potted ball's own score - real
// snooker has no such thing (a ball's worth is fixed no matter how it's
// potted), 'multiply' is a darts-flavored house rule instead. Applies to
// every ball, reds included (though never to a foul's own penalty value -
// see controller.js's foulOpponent/applyThrow).
export const DEFAULT_MULTIPLIER = 'fixed';

// How a turn ends. 'break' (the original, and the default) is real
// snooker's own rule - a pot never ends the turn, only a foul or a miss (or
// the player choosing to stop via End Turn) does, so a turn can run to any
// number of darts. 'alternate' caps a turn at 3 darts, same as every other
// game here, in addition to still ending early on a foul or a miss.
export const DEFAULT_TURN_MODE = 'break';

// Ranked by score - a plain numeric sort, ties just keep player order.
function rankPlayers(players, scores) {
    return players.slice().sort((a, b) => scores[b] - scores[a]);
}

export default {
    id: 'snooker',
    name: 'Snooker',
    description: "Pot the reds, then the colors in order - yellow, green, brown, blue, pink, black. Hit the wrong ball and it's a foul, handing the next player points. Highest score once black is potted wins.",
    minPlayers: 2,
    // Declares this game's setup-screen options (see app.js's
    // renderConfigRows/getConfigRowValues) - whether a ring multiplies a
    // potted ball's score, and how a turn ends. Each option's own
    // `description` is shown under the row once it's selected.
    configRows: [
        {
            id: 'multiplier',
            label: 'Doubles/triples',
            default: DEFAULT_MULTIPLIER,
            options: [
                {
                    value: 'fixed', label: 'Fixed value',
                    description: 'Every ball scores its normal point value, no matter which ring you hit.'
                },
                {
                    value: 'multiply', label: 'Multiply score',
                    description: "A double or triple multiplies that ball's value - including reds."
                }
            ]
        },
        {
            id: 'turnMode',
            label: 'Turn mode',
            default: DEFAULT_TURN_MODE,
            options: [
                {
                    value: 'break', label: 'Break mode',
                    description: 'Keep shooting until you miss, foul, or end your turn - a real snooker break.'
                },
                {
                    value: 'alternate', label: 'Alternate turns',
                    description: 'Turns are capped at 3 darts, same as every other game here.'
                }
            ]
        }
    ],
    rankPlayers
};
